import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { DiffSection } from "@/components/case-checking/ReviewDiffSection";
import { ReviewProgressCompare } from "@/components/case-checking/ReviewProgressCompare";
import { DIFF_SECTION_ORDER, verdictCopy } from "@/components/case-checking/previousReviewDiff";
import { TEXT_BODY, TEXT_MUTED, TEXT_ROW_TITLE } from "@/components/ui/surface/surfaceTokens";
import type { GuidedReviewRestorePreview } from "@/services/api/case-checking";

const VERDICT_SURFACE: Record<
  ReturnType<typeof verdictCopy>["tone"],
  string
> = {
  neutral: "border border-slate-200 bg-slate-50 dark:border-slate-700/80 dark:bg-slate-800/50",
  info: "border border-indigo-200 bg-indigo-50 dark:border-indigo-500/30 dark:bg-indigo-500/10",
  warning: "border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10",
};

export function PreviousReviewDiffPanel({
  preview,
  loading,
  error,
}: {
  preview: GuidedReviewRestorePreview | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <p className={clsx(TEXT_BODY, "flex items-center gap-2")} data-testid="previous-review-loading">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        Loading comparison…
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-rose-600 dark:text-rose-300" data-testid="previous-review-error">
        {error}
      </p>
    );
  }

  if (!preview?.differences) {
    return (
      <p className={TEXT_MUTED} data-testid="previous-review-empty">
        Select a review to compare with your current work.
      </p>
    );
  }

  const diff = preview.differences;
  const verdict = verdictCopy(diff);
  const minimalView = diff.is_current_review || diff.relation === "identical";

  return (
    <div className="space-y-4" data-testid="previous-review-diff-panel">
      <div
        className={clsx("rounded-lg p-4", VERDICT_SURFACE[verdict.tone])}
        data-testid="previous-review-verdict"
        data-tone={verdict.tone}
      >
        <p className={TEXT_ROW_TITLE}>{verdict.title}</p>
        {verdict.detail ? <p className={clsx(TEXT_BODY, "mt-1")}>{verdict.detail}</p> : null}
      </div>

      <ReviewProgressCompare progress={diff.progress} />

      {!minimalView ? (
        <>
          <div className="space-y-5">
            {DIFF_SECTION_ORDER.map((meta) => {
              const section = diff.sections[meta.key];
              const showCall =
                meta.key === "rules" ||
                meta.key === "signoffs" ||
                meta.key === "completeness" ||
                meta.key === "remedials";
              return (
                <DiffSection
                  key={meta.key}
                  title={meta.title}
                  section={section}
                  groupByGroupLabel={meta.key === "rules"}
                  showCall={showCall}
                />
              );
            })}
          </div>

          {diff.not_restorable_rule_ids.length > 0 ? (
            <p
              className="text-sm text-amber-800 dark:text-amber-200/90"
              data-testid="previous-review-wont-restore"
            >
              {diff.not_restorable_rule_ids.length === 1
                ? "1 rule edit on this review is not in the current rubric and won't be restored."
                : `${diff.not_restorable_rule_ids.length} rule edits on this review are not in the current rubric and won't be restored.`}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
